import { useId, type ReactNode } from "react";
import { OTPInput, type SlotProps } from "input-otp";
import { cn } from "../utils/cx.js";
import { Field } from "./Field.js";
import type { InputSize } from "./Input.js";

export interface OtpInputProps {
  /** Number of cells; defaults to a 6-digit TOTP code. */
  length?: number;
  value?: string;
  /** Fires on every keystroke with the partial value. */
  onInput?: (partial: string) => void;
  /** Fires once when every cell is filled — wire auto-submit here. */
  onComplete?: (code: string) => void;
  size?: InputSize;
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  id?: string;
}

const CELL = { sm: "h-9 w-8 text-base", md: "h-10 w-9 text-lg", lg: "h-11 w-10 text-lg" } as const;

/** Segmented one-time-code input (one box per digit) for MFA/verification flows. */
export function OtpInput({
  length = 6,
  value,
  onInput,
  onComplete,
  size = "lg",
  label,
  help,
  error,
  required,
  disabled,
  autoFocus,
  className,
  id,
}: OtpInputProps) {
  const auto = useId();
  const inputId = id ?? auto;
  const invalid = error != null;

  const control = (
    <OTPInput
      id={inputId}
      maxLength={length}
      value={value}
      autoFocus={autoFocus}
      disabled={disabled}
      containerClassName={cn("flex items-center gap-2", className)}
      onChange={(v) => {
        onInput?.(v);
        if (v.length === length) onComplete?.(v);
      }}
      render={({ slots }) => (
        <div className="flex items-center gap-2">
          {slots.map((slot: SlotProps, i: number) => (
            <div
              key={i}
              className={cn(
                "flex items-center justify-center rounded-md border border-input font-medium tabular-nums shadow-xs transition-all",
                CELL[size],
                slot.isActive && "z-10 border-ring ring-[3px] ring-ring/50",
                invalid && "border-destructive",
              )}
            >
              {slot.char ??
                (slot.hasFakeCaret ? <span className="h-5 w-px animate-caret-blink bg-foreground" /> : null)}
            </div>
          ))}
        </div>
      )}
    />
  );

  if (label != null || help != null || error != null) {
    return (
      <Field label={label} required={required} help={help} error={error} htmlFor={inputId}>
        {control}
      </Field>
    );
  }
  return control;
}
